import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Settings2, Clock, X, Plus, MessageSquare, Timer } from 'lucide-react';
import { Switch } from '../ui/switch';
import { FORMATIONS_BY_FORMAT } from '../match/constants';

// Group formations by format for clearer display
const FORMAT_LABELS = {
  '11v11': '11 v 11',
  '9v9': '9 v 9',
  '7v7': '7 v 7',
  '5v5': '5 v 5',
  '3v3': '3 v 3',
};

export const MatchDefaultsSection = ({
  preferredFormation,
  setPreferredFormation,
  defaultKickOffTime,
  setDefaultKickOffTime,
  halfLength,
  setHalfLength,
  extraTimeHalfLength,
  setExtraTimeHalfLength,
  presetReasons,
  setPresetReasons,
  countdownTimerEnabled,
  setCountdownTimerEnabled,
  noteCategories,
  setNoteCategories
}) => {
  const [newReason, setNewReason] = useState('');

  const handleAddReason = () => {
    const trimmed = newReason.trim();
    if (trimmed && !presetReasons.includes(trimmed)) {
      setPresetReasons([...presetReasons, trimmed]);
      setNewReason('');
    }
  };

  const handleRemoveReason = (index) => {
    setPresetReasons(presetReasons.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddReason();
    }
  };

  return (
    <Card data-testid="match-defaults-settings-card">
      <CardHeader>
        <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
          Match Defaults
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-gray-500">
          Set your preferred defaults to save time when adding a new match.
        </p>

        {/* Preferred Formation */}
        <div className="space-y-2">
          <Label htmlFor="preferred-formation" className="text-sm font-medium text-gray-700">
            Preferred Formation
          </Label>
          <Select
            value={preferredFormation || ''}
            onValueChange={setPreferredFormation}
          >
            <SelectTrigger
              className="rounded-xl"
              data-testid="preferred-formation-select"
            >
              <SelectValue placeholder="Select a formation" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(FORMATIONS_BY_FORMAT).map(([format, formations]) => (
                <div key={format}>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {FORMAT_LABELS[format]}
                  </div>
                  {formations.map((f) => (
                    <SelectItem key={`${format}-${f}`} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Default Kick-off Time */}
        <div className="space-y-2">
          <Label htmlFor="default-kickoff-time" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
            Most Common Kick-off Time
          </Label>
          <Input
            id="default-kickoff-time"
            type="time"
            value={defaultKickOffTime || ''}
            onChange={(e) => setDefaultKickOffTime(e.target.value)}
            className="rounded-xl max-w-[200px]"
            data-testid="default-kickoff-time-input"
          />
        </div>

        {/* Half Length */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
            Half Length (minutes)
          </Label>
          <Input
            type="number"
            min="1"
            max="90"
            value={halfLength || ''}
            onChange={(e) => setHalfLength(parseInt(e.target.value) || '')}
            placeholder="45"
            className="rounded-xl max-w-[200px]"
            data-testid="half-length-input"
          />
          <p className="text-xs text-gray-400">Default duration for each half. Used by the matchday live timer.</p>
        </div>

        {/* Extra Time Half Length */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
            Extra Time Half Length (minutes)
          </Label>
          <Input
            type="number"
            min="1"
            max="30"
            value={extraTimeHalfLength || ''}
            onChange={(e) => setExtraTimeHalfLength(parseInt(e.target.value) || '')}
            placeholder="15"
            className="rounded-xl max-w-[200px]"
            data-testid="extra-time-half-length-input"
          />
          <p className="text-xs text-gray-400">Default duration for each half of extra time.</p>
        </div>

        {/* Preset Non-Selection Reasons */}
        <div className="space-y-3 pt-2 border-t border-gray-100">
          <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
            <MessageSquare className="w-4 h-4 text-orange-400" strokeWidth={1.5} />
            Non-Selection Quick Reasons
          </Label>
          <p className="text-xs text-gray-400">
            Quick-select reasons shown when marking a player as not selected for a match.
          </p>

          {/* Current presets */}
          <div className="flex flex-wrap gap-2" data-testid="preset-reasons-list">
            {presetReasons.map((reason, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200 group"
              >
                {reason}
                <button
                  type="button"
                  onClick={() => handleRemoveReason(index)}
                  className="text-orange-400 hover:text-red-500 transition-colors ml-0.5"
                  data-testid={`remove-preset-${index}`}
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </span>
            ))}
          </div>

          {/* Add new preset */}
          <div className="flex gap-2">
            <Input
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add a reason..."
              className="rounded-xl flex-1 text-sm"
              data-testid="new-preset-reason-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleAddReason}
              disabled={!newReason.trim()}
              className="rounded-xl border-orange-200 text-orange-600 hover:bg-orange-50"
              data-testid="add-preset-reason-btn"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        {/* Countdown Timer Toggle */}
        {typeof setCountdownTimerEnabled === 'function' && (
          <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-100">
            <div className="flex items-start gap-2 min-w-0">
              <Timer className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div className="min-w-0">
                <Label className="text-sm font-medium text-gray-800">Next Match countdown timer</Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Show the live DAYS / HRS / MIN / SEC countdown on the Clubhouse Next Match card.
                </p>
              </div>
            </div>
            <Switch
              checked={!!countdownTimerEnabled}
              onCheckedChange={setCountdownTimerEnabled}
              data-testid="countdown-timer-toggle"
            />
          </div>
        )}

        {/* Coach Note Categories */}
        {typeof setNoteCategories === 'function' && (
          <div className="pt-4 border-t border-gray-100">
            <Label className="text-sm font-medium text-gray-800">Coach's Notes categories</Label>
            <p className="text-xs text-gray-500 mt-0.5 mb-3">
              Categories you can tag each match note with (e.g. Shape, Defence, Set Pieces).
            </p>
            <NoteCategoriesEditor categories={noteCategories || []} setCategories={setNoteCategories} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

function NoteCategoriesEditor({ categories, setCategories }) {
  const [newValue, setNewValue] = useState('');

  const add = () => {
    const v = newValue.trim();
    if (!v) return;
    if (categories.includes(v)) { setNewValue(''); return; }
    setCategories([...categories, v]);
    setNewValue('');
  };
  const remove = (cat) => setCategories(categories.filter(c => c !== cat));

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {categories.length === 0 ? (
          <span className="text-xs text-gray-400 italic">No categories yet.</span>
        ) : categories.map(c => (
          <span key={c} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm" data-testid={`note-category-${c}`}>
            {c}
            <button
              type="button"
              onClick={() => remove(c)}
              className="ml-1 p-1 rounded-full text-emerald-700/60 hover:bg-emerald-100 hover:text-emerald-800"
              aria-label={`Remove ${c}`}
              data-testid={`note-category-remove-${c}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add category (e.g. Pressing)"
          className="flex-1 rounded-xl"
          data-testid="note-category-input"
        />
        <Button type="button" onClick={add} variant="outline" className="rounded-xl" disabled={!newValue.trim()} data-testid="note-category-add-btn">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default MatchDefaultsSection;
